import { useEffect, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import FlatPickr from "react-flatpickr";
import {
  AlertCircle,
  BarChart3,
  CheckCircle2,
  Clock,
  Download,
  LucideIcon,
  RotateCcw,
  Search,
  Ticket,
  UserCheck,
  XCircle,
} from "lucide-react";
import { downloadTicketByBookingNo, getAllVenueBySlug, getReport } from "../../../api";
import ExportToExcel from "../../../components/ExportToExcel/ExportToExcel";
import FormattedDate from "../../../components/common/DateFormat/FormattedDate";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import TablesV2 from "../../../components/common/Tables/TablesV2";
import CommonSelect from "../../../components/common/Select/CommonSelect";
import { handleError } from "../../../utils/errorHandler";
import { useNightMode } from "../../../contexts/NightModeContext";
import Background from "../../Login/Background";
import { PulseLoader } from "react-spinners";

interface Booking {
  bookingStatus: string;
  bookedBy: string;
  totalTicketCount: number;
}

/** Status counts from the venue-scoped booking list. */
interface StatusCounts {
  successful: number;
  attended: number;
  pending: number;
  failed: number;
  cancelled: number;
  total: number;
}

const emptyCounts: StatusCounts = {
  successful: 0,
  attended: 0,
  pending: 0,
  failed: 0,
  cancelled: 0,
  total: 0,
};

/** Count confirmed / pending / failed / cancelled / attended / total. */
const computeStatusCounts = (bookings: Booking[]): StatusCounts => {
  let successful = 0;
  let cancelled = 0;
  let pending = 0;
  let failed = 0;
  let attended = 0;

  bookings.forEach((booking) => {
    if (booking.bookingStatus === "confirmed") {
      successful++;
      attended += Number(booking.totalTicketCount) || 0;
    } else if (booking.bookingStatus === "cancelled") cancelled++;
    else if (booking.bookingStatus === "pending") pending++;
    else if (booking.bookingStatus === "failed") failed++;
  });

  return { successful, attended, pending, failed, cancelled, total: bookings.length };
};

const STAT_CARDS: { key: keyof StatusCounts; label: string; icon: LucideIcon; tone: string }[] = [
  { key: "successful", label: "SUCCESSFUL", icon: CheckCircle2, tone: "green" },
  { key: "attended", label: "ATTENDED", icon: UserCheck, tone: "pink" },
  { key: "pending", label: "PENDING", icon: Clock, tone: "orange" },
  { key: "failed", label: "FAILED", icon: AlertCircle, tone: "purple" },
  { key: "cancelled", label: "CANCELLED", icon: XCircle, tone: "orange" },
  { key: "total", label: "TOTAL", icon: Ticket, tone: "pink" },
];

/**
 * Booking Status Report — primary bookings for the current event.
 * Shell matches Attendee Report v3 (Background, PageHeader, stats, filters, TablesV2).
 */
const BookingStatusReport = () => {
  const { isNightMode } = useNightMode();
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [book, setBookNo] = useState("");
  const [fullBookingList, setFullBookingList] = useState<any[]>([]);
  const [bookingStatus, setBookingStatus] = useState<any[]>([]);
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();
  const [downloadingTicket, setDownloadingTicket] = useState(false);
  const [eventVenues, setEventVenues] = useState<any[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<string>("0");
  const [counts, setCounts] = useState<StatusCounts>(emptyCounts);

  const getEventVenue = async (slugId: string) => {
    try {
      const resp = await getAllVenueBySlug(slugId);
      const venueOptions = (resp || []).map((item: any) => ({
        value: String(item.venueId),
        label: item.venueName,
      }));
      setEventVenues([{ value: "0", label: "All Venues" }, ...venueOptions]);
    } catch (error) {
      handleError(error);
    }
  };

  const generateExportData = (data: any[]) =>
    data.map((item: any) => ({
      bookingNumber: item?.bookingNo || "",
      firstName: item?.Contact?.firstName || "",
      email: item?.Contact?.email || "",
      phoneNo: item?.Contact?.phoneNo || "",
      bookingStatus: item?.bookingStatus || "",
      totalTicketCount: item?.totalTicketCount || "",
      ticketPrice: item?.TicketType?.price || "",
      discountAmount: item?.discountAmount || "",
      bookingDate: item?.createdAt || "",
      venueName: item?.Venue?.venueName || "",
    }));

  /** Apply venue-scoped list and refresh metric cards. */
  const applyBookingList = (list: any[]) => {
    setFullBookingList(list);
    setBookingStatus(list);
    setCounts(computeStatusCounts(list));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const eventData = localStorage.getItem("eventData");
        if (!eventData) return;

        const parsed = JSON.parse(eventData);
        const [reportResponse] = await Promise.all([
          getReport(parsed.eventId),
          getEventVenue(parsed.slugId),
        ]);

        // Primary bookings only (bookedBy === "0")
        applyBookingList((reportResponse || []).filter((item: any) => item.bookedBy === "0"));
      } catch (error) {
        console.error("Error fetching booking status report:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const onSelectedVenue = async (venueId: string) => {
    setIsLoading(true);
    try {
      const eventData = localStorage.getItem("eventData");
      if (!eventData) return;
      const eventSlug = JSON.parse(eventData).eventId;
      const reportResponse = await getReport(eventSlug);
      const activeBooking =
        venueId !== "0"
          ? reportResponse.filter(
              (item: any) => item.bookedBy === "0" && String(item.venueId) === venueId
            )
          : reportResponse.filter((item: any) => item.bookedBy === "0");
      applyBookingList(activeBooking);
      setSelectedVenue(venueId);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Client-side: booking no, booked-by name, date range
  useEffect(() => {
    const filtered = fullBookingList.filter((row: any) => {
      const bookno = (row?.bookingNo || "").toLowerCase();
      const name = (row?.Contact?.firstName || "").toLowerCase();
      const bookingDate = new Date(row?.createdAt);

      if (fromDate && bookingDate < new Date(fromDate)) return false;
      if (toDate) {
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        if (bookingDate > end) return false;
      }

      const qBook = book.trim().toLowerCase();
      const qName = search.trim().toLowerCase();
      return (!qBook || bookno.startsWith(qBook)) && (!qName || name.startsWith(qName));
    });
    setBookingStatus(filtered);
  }, [book, search, fromDate, toDate, fullBookingList]);

  const clearFilters = () => {
    setSearch("");
    setBookNo("");
    setFromDate(undefined);
    setToDate(undefined);
    if (selectedVenue !== "0") onSelectedVenue("0");
  };

  const hasActiveFilters =
    Boolean(search || book || fromDate || toDate) || selectedVenue !== "0";

  const handleDownloadTicket = async (row: any) => {
    try {
      setDownloadingTicket(true);
      const bookingNo = row?.primaryBookingNo || row?.bookingNo;
      if (!bookingNo) throw new Error("Booking number not found");
      const response = await downloadTicketByBookingNo(bookingNo);
      if (!response) throw new Error("Failed to download ticket");
      const blob = new Blob([response], { type: "application/pdf" });
      window.open(window.URL.createObjectURL(blob), "_blank");
    } catch (err) {
      handleError(err);
    } finally {
      setDownloadingTicket(false);
    }
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "bookingNo",
        header: "BOOKED NO",
        cell: ({ row }) => (
          <span className="font-mono contact-name-text fs-12px">{row.original?.bookingNo || "—"}</span>
        ),
      },
      {
        id: "bookedBy",
        header: "BOOKED BY",
        accessorFn: (row) => row?.Contact?.firstName || row?.Contact?.email || "",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="d-flex flex-column gap-1">
              <span className="fw-bold contact-name-text fs-13px">{item?.Contact?.firstName || "Guest"}</span>
              <span className="font-mono contact-sub-text fs-11px">{item?.Contact?.email || "—"}</span>
              <span className="font-mono contact-sub-text fs-11px">{item?.Contact?.phoneNo || "—"}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "bookingStatus",
        header: "STATUS",
        cell: ({ row }) => (
          <span className="contact-sub-text fs-12px text-capitalize">{row.original?.bookingStatus || "—"}</span>
        ),
      },
      {
        accessorKey: "totalTicketCount",
        header: "TICKETS",
        cell: ({ row }) => (
          <span className="font-mono contact-name-text fs-12px">{row.original?.totalTicketCount ?? 0}</span>
        ),
      },
      {
        id: "ticketAmount",
        header: "TICKET AMOUNT",
        accessorFn: (row) => row?.TicketType?.price ?? 0,
        cell: ({ row }) => (
          <span className="font-mono contact-sub-text fs-12px">{row.original?.TicketType?.price ?? 0}</span>
        ),
      },
      {
        accessorKey: "discountAmount",
        header: "DISCOUNT",
        cell: ({ row }) => (
          <span className="font-mono contact-sub-text fs-12px">{row.original?.discountAmount ?? 0}</span>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "BOOKED DATE",
        cell: ({ row }) =>
          row.original?.createdAt ? (
            <span className="font-mono contact-sub-text fs-12px">
              <FormattedDate dateString={row.original.createdAt} />
            </span>
          ) : (
            <span className="contact-sub-text fs-12px">N/A</span>
          ),
      },
      {
        id: "action",
        header: "ACTION",
        enableSorting: false,
        cell: ({ row }) =>
          row.original?.bookingStatus === "confirmed" ? (
            <button
              type="button"
              className="contact-action-btn"
              onClick={() => handleDownloadTicket(row.original)}
              disabled={downloadingTicket}
            >
              <Download size={14} /> Ticket
            </button>
          ) : (
            <span className="contact-sub-text fs-12px">—</span>
          ),
      },
    ],
    [downloadingTicket]
  );

  return (
    <div className={`contact-list-container ${isNightMode ? "text-white" : "text-dark"}`}>
      <Background />

      <PageHeader
        title="Booking Status Report"
        subtitle="Review booking outcomes by venue, booking number, booker, or date range."
        icon={BarChart3}
        isLoading={isLoading}
        actions={
          <ExportToExcel
            data={generateExportData(bookingStatus)}
            fileName="BookingStatus_Report"
            buttonText="Export (.CSV)"
          />
        }
      />

      <div className="contact-stats-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
        {STAT_CARDS.map(({ key, label, icon: Icon, tone }) => (
          <div className="contact-stat-card" key={key}>
            <div>
              <div className="contact-stat-label">{label}</div>
              <div className="contact-stat-value">{isLoading ? "..." : counts[key]}</div>
            </div>
            <div className={`contact-stat-icon ${tone}`}>
              <Icon size={22} />
            </div>
          </div>
        ))}
      </div>

      <div className="contact-filter-card attendee-report-filters">
        <div className="attendee-filter-grid">
          {eventVenues.length > 1 && (
            <div className="attendee-filter-field">
              <CommonSelect
                options={eventVenues}
                value={selectedVenue}
                onChange={(e) => onSelectedVenue(e.target.value)}
                label="Event Venue"
              />
            </div>
          )}

          <div className="attendee-filter-field position-relative">
            <label className="subuser-stat-label">Booking Number</label>
            <Search className="attendee-filter-icon contact-sub-text" size={16} />
            <input
              type="text"
              className="contact-search-input attendee-filter-input"
              placeholder="Search by booked no"
              value={book}
              onChange={(e) => setBookNo(e.target.value)}
            />
          </div>

          <div className="attendee-filter-field position-relative">
            <label className="subuser-stat-label">Booked By</label>
            <Search className="attendee-filter-icon contact-sub-text" size={16} />
            <input
              type="text"
              className="contact-search-input attendee-filter-input"
              placeholder="Search by booked by"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="attendee-filter-field">
            <label className="subuser-stat-label">Start Date</label>
            <FlatPickr
              className="contact-search-input attendee-date-input"
              value={fromDate}
              onChange={([date]) => setFromDate(date)}
              options={{ dateFormat: "Y-m-d" }}
              placeholder="Select start date"
            />
          </div>

          <div className="attendee-filter-field">
            <label className="subuser-stat-label">End Date</label>
            <FlatPickr
              className="contact-search-input attendee-date-input"
              value={toDate}
              onChange={([date]) => setToDate(date)}
              options={{ dateFormat: "Y-m-d" }}
              placeholder="Select end date"
            />
          </div>
        </div>

        {hasActiveFilters && (
          <div className="d-flex justify-content-end mt-3">
            <button type="button" className="contact-action-btn" onClick={clearFilters}>
              <RotateCcw size={14} /> Clear Filters
            </button>
          </div>
        )}
      </div>

      {downloadingTicket && (
        <div className="d-flex align-items-center justify-content-center py-2 gap-2">
          <PulseLoader color="#EE3F7C" size={8} />
          <span className="contact-sub-text fs-12px">Preparing ticket PDF…</span>
        </div>
      )}

      <TablesV2
        data={bookingStatus}
        columns={columns}
        isLoading={isLoading}
        getRowId={(row, idx) => String(row.bookingId || row.bookingNo || `book-${idx}`)}
        loadingMessage="Loading booking status report..."
        emptyMessage="No bookings match your current filters."
        pageSize={25}
      />
    </div>
  );
};

export default BookingStatusReport;
